import { Dispatch, MutableRefObject, SetStateAction, useEffect, useState } from "react";
import { useMutation } from "react-query";
import Graphic from "@arcgis/core/Graphic";
import { mapValues, keyBy, Dictionary, partition } from "lodash";
import { geojsonToArcGIS } from "@terraformer/arcgis";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { useMapLayerFeatures } from "../useMapLayerFeatures";
import { bulkUpdateMapFeature, BulkUpdateParams, LayerFieldDefinition } from "../../queries/maps";
import useNetworkHeaders from "../../../../hooks/useNetworkHeaders";
import { FeatureTracking } from "./useSketchEvents";
import { convertToGeoJSON, getArcGISGeometryType, getSymbolForGeometryType } from "../../utils/arcgisHelper";
import { batchFeatures } from "../../utils/geojsonHelper";

interface UseMapFeaturesOptions {
  layerId?: number | string;
  editLayerRef: MutableRefObject<GraphicsLayer | null>;
  featureTracking: FeatureTracking;
  setFeatureTracking: Dispatch<SetStateAction<FeatureTracking>>;
  fieldDefinitions: LayerFieldDefinition[];
  prepareFieldsForSubmission: (
    graphic: Graphic,
    oldFieldDefs: LayerFieldDefinition[],
    newFieldDefs: LayerFieldDefinition[],
  ) => {
    id: number;
    value: any;
  }[];
  saveFieldDefinitions?: (newLayerId?: string | number) => Promise<any>;
  hasUnsavedFieldChanges?: boolean;
  maxFeatureIndex: MutableRefObject<number>;
  markFeatureAsChangedDebounced: ((graphicId: string) => void) | null;
}

export function useMapFeatures({
  layerId,
  editLayerRef,
  featureTracking,
  setFeatureTracking,
  fieldDefinitions,
  prepareFieldsForSubmission,
  saveFieldDefinitions,
  hasUnsavedFieldChanges,
  maxFeatureIndex,
  markFeatureAsChangedDebounced,
}: UseMapFeaturesOptions) {
  const [isSaving, setIsSaving] = useState(false);
  const { mapFeaturesQuery } = layerId
    ? useMapLayerFeatures(layerId)
    : {
        mapFeaturesQuery: undefined,
      };
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  const networkHeaders = useNetworkHeaders();
  const [bulkUpdate] = useMutation(bulkUpdateMapFeature(networkHeaders), {
    onSuccess: () => mapFeaturesQuery?.refetch(),
    throwOnError: true,
  });

  // Load features from query result to map
  useEffect(() => {
    if (mapFeaturesQuery?.data && editLayerRef.current) {
      editLayerRef.current.removeAll();
      setFeatureTracking({});

      if (!mapFeaturesQuery.data) return;

      const graphics = mapFeaturesQuery.data
        .sort((a, b) => a.id - b.id)
        .map((feature, index) => {
          const graphicId = `existing-${feature.id}-${Date.now()}`;
          const featureAttributes = mapValues(keyBy(feature.customFields, "id"), (value) => value.value);

          const graphic = new Graphic({
            geometry: {
              ...geojsonToArcGIS(feature.geometry as GeoJSON.GeoJSON),
              type: getArcGISGeometryType(feature.geometry.type),
            },
            attributes: {
              ...featureAttributes,
              featureId: feature.id,
              graphicId,
              index,
            },
            symbol: getSymbolForGeometryType(feature.geometry.type) as any,
          });

          markFeatureAsChangedDebounced?.(graphicId);

          // Track the existing feature
          setFeatureTracking((prev) => ({
            ...prev,
            [graphicId]: {
              featureId: feature.id,
              originalGeometry: feature.geometry,
            },
          }));

          return graphic;
        });

      maxFeatureIndex.current = graphics.length;
      editLayerRef.current.graphics.addMany(graphics);
    }
    setFeaturesLoaded(true);
  }, [mapFeaturesQuery?.data, !!editLayerRef.current]);

  // Save features (create, update, delete)
  const saveFeatures = async (newLayerId?: string | number) => {
    if (!editLayerRef.current) return;
    try {
      setIsSaving(true);

      // First, save field definitions if there are unsaved changes
      const oldFieldDefinitions = fieldDefinitions;
      let updatedFieldDefinitions = fieldDefinitions;
      if (hasUnsavedFieldChanges && saveFieldDefinitions) {
        updatedFieldDefinitions = (await saveFieldDefinitions(newLayerId))[0].fieldDefinitions;
      }

      const graphics = editLayerRef.current.graphics.toArray();
      const graphicIdMap = keyBy(graphics, (g) => g.attributes?.graphicId) as Dictionary<Graphic>;
      type IndexedCreates = Extract<BulkUpdateParams[number], { mode: "Create" }> & {
        index: number; // We need to sort this before sending it so created features are still in order
      };
      const bulkUpdatePayload: (Exclude<BulkUpdateParams[number], { mode: "Create" }> | IndexedCreates)[] = [];

      await Promise.all(
        Object.entries(featureTracking).map(async ([graphicId, tracking]) => {
          const graphic = graphicIdMap[graphicId];
          if (!graphic && !tracking.isDeleted) return;

          if (tracking.isDeleted && tracking.featureId) {
            bulkUpdatePayload.push({
              mode: "Delete",
              featureId: tracking.featureId,
            });
            return;
          }

          if (!graphic?.geometry) return;
          const geoJSONGeometry = await convertToGeoJSON(graphic.geometry);
          const fieldValues = prepareFieldsForSubmission(graphic, oldFieldDefinitions, updatedFieldDefinitions);

          if (tracking.isNew) {
            bulkUpdatePayload.push({
              mode: "Create",
              geometry: geoJSONGeometry,
              fields: fieldValues,
              index: graphic.attributes.index,
            });
            return;
          }

          if (tracking.isModified && tracking.featureId) {
            bulkUpdatePayload.push({
              mode: "Edit",
              featureId: tracking.featureId,
              geometry: geoJSONGeometry,
              fields: fieldValues,
            });
            return;
          }
        }),
      );

      const [createObjects, editDeleteObjects] = partition(
        bulkUpdatePayload,
        (payload) => payload.mode == "Create",
      ) as [IndexedCreates[], BulkUpdateParams];
      const sortedCreates = createObjects.sort((a, b) => a.index - b.index);
      const data = (sortedCreates as BulkUpdateParams).concat(editDeleteObjects);

      // Batch to avoid payload size limit
      const batches = batchFeatures(data, 900 * 1024); // 900KB
      for (const batch of batches) {
        await bulkUpdate({ layerId: newLayerId ?? layerId!, data: batch });
      }

      await mapFeaturesQuery?.refetch();
      setIsSaving(false);
    } catch (error) {
      console.error("Error saving features:", error);
      setIsSaving(false);
      throw error;
    }
  };

  return {
    isSaving,
    saveFeatures,
    areFeaturesLoading: mapFeaturesQuery?.isLoading || !featuresLoaded,
  };
}
